import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { useDispatch, useSelector } from 'react-redux';

import {
	BGHorDivider,
	BGTimerWrapper,
	BGVertDividerMobile,
	BtnCloseModal,
} from '../../../../assets';
import Centered from '../../../../shared/Centered';
import { RootState } from '../../../../store';
import { profileRequest } from '../../../../store/auth/authActions';
import { selectIsLogged } from '../../../../store/auth/authReducer';
import { bonusInitRequest } from '../../../../store/bonus/bonusActions';
import { miniGamesInitRequest } from '../../../../store/miniGames';
import { env } from '../../../../utils/env';
import { inset } from '../../../../utils/helpers/css';
import { useBonusCountdown } from '../../../../utils/hooks/useBonusCountdown';
import { useDevice } from '../../../../utils/hooks/useDevice';
import LS from '../../../../utils/localStorageUtils';
import { getInsetAdjustedOffset } from '../../../../utils/safeArea';
import { globalBroadcastToastConfig } from '../../../navigation/globalBroadcastToastConfig';
import { RootStackParamList } from '../../../navigator';
import { IndicatorScrollView } from '../../common/ScrollIndicator';
import SvaraModal from '../SvaraModal';
import BGCollectButton from './BGCollectButton';
import BGDay from './BGDay';
import BGDayMobile from './BGDayMobile';
import BGInfoButton from './BGInfoButton';
import BGJackpotLabel from './BGJackpotLabel';
import BGLogo from './BGLogo';
import { getPlatformUI } from './BGPlatformUI';
import DailyBonusOverlayAnimation from './DailyBonusOverlayAnimation';
import BGBlackAndRedGame from './games/BGBlackAndRed';
import BGScratchGame from './games/BGScratchGame';
import BGSlotGame from './games/BGSlotGame';
import MiniGameModalProps from './games/MiniGameModal';
import { bonusGamesModalStyles } from './styles';
import { DayCount } from './utils';

interface BonusGamesModalProps {
	onClose: () => void;
}

const DEFAULT_BONUS_AMOUNTS = [500, 750, 1000, 1250, 1500];
const DEFAULT_VIP_BONUS_AMOUNTS = [500, 750, 1000, 1250, 1500];

const BonusGamesModal = ({ onClose }: BonusGamesModalProps) => {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const isLogged = useSelector(selectIsLogged);
	const miniGameConfig = {
		url: `${env('SVARA_API_URL')}/api/game`,
		token: LS.get('access_token') || '',
		language: LS.get('lang') || 'en',
	};

	const { width, insets, isDesktop, isWeb, isIOS } = useDevice();
	const [gamePath, setGamePath] = useState<string | null>(null);
	const [showInfoModal, setShowInfoModal] = useState(false);
	const { t } = useTranslation();

	const dispatch = useDispatch();
	const requestedRef = useRef(false);
	useEffect(() => {
		if (!isLogged) {
			return;
		}

		if (requestedRef.current) {
			console.log('Bonus already requested, skipping...');
			return;
		}
		requestedRef.current = true;
		dispatch(bonusInitRequest());
		dispatch(profileRequest());
		dispatch(miniGamesInitRequest());
	}, [dispatch, isLogged]);

	const playerProfile = useSelector((s: RootState) => s.auth.user);
	const bonus = useSelector((s: RootState) => s.bonus.bonus);
	const miniGames = useSelector((s: RootState) => s.miniGames.games);

	const collectedAt = useSelector((s: RootState) => s.auth.user?.collectedAt) as string | undefined;

	const bonusHours = useSelector((s: RootState) => s.auth.user?.bonusHours) as string | undefined;

	const { timeText, ready } = useBonusCountdown(collectedAt, Number(bonusHours));

	const dailyArray = useMemo(() => {
		const bonusAmounts = Array.isArray(bonus?.bonusAmounts)
			? bonus.bonusAmounts
			: DEFAULT_BONUS_AMOUNTS;
		const vipAmounts = Array.isArray(bonus?.vipAmounts)
			? bonus.vipAmounts
			: DEFAULT_VIP_BONUS_AMOUNTS;

		return [1, 2, 3, 4, 5].map(day => ({
			day,
			chips: bonusAmounts[day - 1] ?? 0,
			vipChips: vipAmounts[day - 1] ?? 0,
		}));
	}, [bonus]);

	const currentDay = useSelector((s: RootState) => {
		const prevDays = s.bonus.bonus?.previous_days_collected;
		if (prevDays === undefined || prevDays === null) return undefined;
		return prevDays + 1;
	});

	const normalizedCurrentDay: DayCount | undefined = useMemo(() => {
		if (typeof currentDay !== 'number') return undefined;

		// currentDay comes from: previous_days_collected + 1
		// So if previous_days_collected = 0, currentDay = 1 (Day 1 claimed)
		// We want to show Day 2 as current

		// If all 5 days collected (currentDay = 5), keep showing Day 5 as current
		if (currentDay >= 5) {
			return 1 as DayCount; // Day 1 is next
		}

		// If no days collected yet (currentDay = 0), show Day 1 as current
		if (currentDay <= 0) {
			return 1 as DayCount;
		}

		// Otherwise, show the next day to collect as current
		const nextDay = currentDay + 1;
		console.log(`Normal progression, next day: ${nextDay}`);
		return nextDay as DayCount;
	}, [currentDay]);

	const { containerScale, isSmall } = useMemo(() => {
		let scale = 1;

		if (width > 1900) scale = width / 700 / 2.1;
		else if (width > 1500) scale = width / 700 / 1.8;
		else if (width > 1200) scale = width / 700 / 1.5;
		else if (width > 850) scale = width / 700 / 1.3;
		else if (width > 760) scale = 0.86;
		else if (width > 650) scale = 0.7;
		else scale = width / 930;

		return { containerScale: scale, isSmall: width < 700 };
	}, [width]);
	const toastBottomOffset = getInsetAdjustedOffset(insets.bottom, 10);

	const ui = useMemo(
		() =>
			getPlatformUI({
				isSmall,
				containerScale,
				width,
				isDesktop,
				isWeb,
			}),
		[isSmall, containerScale, width, isDesktop, isWeb],
	);
	const handleRequireLogin = () => {
		onClose();
		requestAnimationFrame(() => navigation.navigate('Login'));
	};

	return (
		<>
			<SvaraModal
				visible={!gamePath}
				onClose={onClose}
				closeIcon={BtnCloseModal}
				{...ui.modalProps}
			>
				<BGInfoButton isSmall={isSmall} onPress={() => setShowInfoModal(true)} />

				<View style={[bonusGamesModalStyles.container, isSmall ? ui.smallContainerStyle : null]}>
					<BGLogo isSmall={isSmall} />
					<BGJackpotLabel isSmall={isSmall} />

					{!isSmall && (
						<>
							<View style={styles.gamesRowTop}>
								<BGBlackAndRedGame
									onStartGame={setGamePath}
									canPlay={isLogged}
									onRequireLogin={handleRequireLogin}
									config={miniGameConfig}
									miniGameData={miniGames?.black_and_red}
									playerLevel={playerProfile?.level || 1}
								/>
								<BGScratchGame
									onStartGame={setGamePath}
									canPlay={isLogged}
									onRequireLogin={handleRequireLogin}
									config={miniGameConfig}
									miniGameData={miniGames?.scratch_card}
									playerLevel={playerProfile?.level || 1}
								/>
								<BGSlotGame
									onStartGame={setGamePath}
									canPlay={isLogged}
									onRequireLogin={handleRequireLogin}
									config={miniGameConfig}
									miniGameData={miniGames?.slot}
									playerLevel={playerProfile?.level || 1}
								/>
							</View>

							<View style={styles.gamesRowBottom}>
								{dailyArray?.map(d => (
									<BGDay
										key={d.day}
										day={d.day as DayCount}
										bonus={d.chips}
										vipBonus={d.vipChips}
										currentDay={normalizedCurrentDay}
										isVip={playerProfile?.isVip}
									/>
								))}

								{!isLogged || ready ? (
									<BGCollectButton isLogged={isLogged} onRequireLogin={handleRequireLogin} />
								) : (
									<>
										<View style={styles.collectColumn}>
											<View style={styles.collectHeadingWrap}>
												<Text
													style={[
														styles.collectHeadingText,
														{
															fontSize: isSmall ? 14 : 16,
															fontFamily: isIOS ? 'Arial' : 'sans-serif',
														},
													]}
												>
													{t('T_SITE_BONUSES_HEADING')}
												</Text>
											</View>
											<Image
												source={BGHorDivider}
												resizeMode='contain'
												style={[
													styles.horDivider,
													{
														maxWidth: isSmall ? 220 : undefined,
													},
												]}
											/>

											<View style={styles.timerSectionPad}>
												<View style={styles.timerBoxOuter}>
													<Image
														source={BGTimerWrapper}
														resizeMode='stretch'
														style={styles.timerImgScaled}
													/>
													<Centered style={{ ...inset(0), position: 'absolute' }}>
														<Text
															style={[styles.timerLabel]}
															numberOfLines={1}
															ellipsizeMode='tail'
														>
															{timeText}
														</Text>
													</Centered>
												</View>
											</View>
										</View>
									</>
								)}
							</View>
						</>
					)}

					{isSmall && (
						<View style={styles.smallMobileWrap}>
							<View style={styles.vertDividerWrap}>
								<Image style={styles.vertDividerImg} source={BGVertDividerMobile} />
							</View>

							<View style={styles.mobileGamesRow}>
								<View style={styles.mobileLeftCol}>
									<BGBlackAndRedGame
										isSmall
										onStartGame={setGamePath}
										canPlay={isLogged}
										onRequireLogin={handleRequireLogin}
										config={miniGameConfig}
										miniGameData={miniGames?.black_and_red}
										playerLevel={playerProfile?.level || 1}
									/>
									<BGScratchGame
										isSmall
										onStartGame={setGamePath}
										canPlay={isLogged}
										onRequireLogin={handleRequireLogin}
										config={miniGameConfig}
										miniGameData={miniGames?.scratch_card}
										playerLevel={playerProfile?.level || 1}
									/>
									<BGSlotGame
										isSmall
										onStartGame={setGamePath}
										canPlay={isLogged}
										onRequireLogin={handleRequireLogin}
										config={miniGameConfig}
										miniGameData={miniGames?.slot}
										playerLevel={playerProfile?.level || 1}
									/>
								</View>

								<View style={styles.mobileRightCol}>
									<View
										style={[
											styles.mobileCollectSection,
											isWeb ? styles.mobileCollectSectionWeb : null,
										]}
									>
										{!isLogged || ready ? (
											<BGCollectButton
												isSmall
												isLogged={isLogged}
												onRequireLogin={handleRequireLogin}
											/>
										) : (
											<>
												<Image
													source={BGHorDivider}
													resizeMode='contain'
													style={[styles.horDivider, { maxWidth: isSmall ? 140 : undefined }]}
												/>
												<View style={styles.collectHeadingWrapTight}>
													<Text
														style={[
															styles.collectHeadingText,
															{
																fontSize: isSmall ? 14 : 16,
																fontFamily: isIOS ? 'Arial' : 'sans-serif',
															},
														]}
													>
														{t('T_SITE_BONUSES_HEADING')}
													</Text>
												</View>
												<Image
													source={BGHorDivider}
													resizeMode='contain'
													style={[styles.horDivider, { maxWidth: isSmall ? 140 : undefined }]}
												/>
												{ui.renderSmallTimer({ timeText })}
											</>
										)}
									</View>

									{isWeb ? null : <View style={styles.nativeSpacer4} />}
									<View style={[styles.mobileDaysList, isWeb ? styles.mobileDaysListWeb : null]}>
										{(isWeb ? dailyArray?.slice(0, 5) : dailyArray?.slice(0, 5)).map(d => (
											<View
												key={d.day}
												style={[
													styles.mobileDayItemWrap,
													!isWeb ? styles.mobileDayItemWrapNative : null,
													isWeb ? styles.mobileDayItemWrapWeb : null,
												]}
											>
												<BGDayMobile
													day={d.day as DayCount}
													bonus={d.chips}
													vipBonus={d.vipChips}
													currentDay={normalizedCurrentDay}
													isSmall={isSmall}
													isVip={playerProfile?.isVip}
												/>
											</View>
										))}
									</View>
								</View>
							</View>
						</View>
					)}

					{(!isWeb || !isDesktop) && (
						<View style={styles.toastInModal} pointerEvents='box-none'>
							<Toast
								config={globalBroadcastToastConfig}
								position='bottom'
								bottomOffset={toastBottomOffset}
							/>
						</View>
					)}

					<DailyBonusOverlayAnimation />
				</View>
			</SvaraModal>

			{gamePath && (
				<MiniGameModalProps
					width={width}
					gamePath={gamePath}
					onClose={() => {
						setGamePath(null);
						dispatch(miniGamesInitRequest());
					}}
				/>
			)}

			<SvaraModal
				visible={showInfoModal}
				onClose={() => setShowInfoModal(false)}
				closeIcon={BtnCloseModal}
				closeButtonSize='big'
				closeButtonStyles={{
					position: 'absolute',
					zIndex: 100,
					width: 48,
					height: 48,
					top: isSmall ? -23 : -23,
					right: isSmall ? -2 : 20,
					transform: [{ scale: isSmall ? 1 : 1 }],
				}}
				containerStyles={{
					maxWidth: isDesktop ? 800 : width * 0.9,
					marginTop: isSmall ? 30 : 60,
				}}
				overlayStyle={{
					backgroundColor: 'rgba(0,0,0,0.9)',
				}}
			>
				<IndicatorScrollView
					style={styles.infoScroll}
					contentContainerStyle={[
						styles.infoScrollContent,
						{
							paddingHorizontal: isDesktop ? 40 : 20,
							maxWidth: isDesktop ? 800 : '100%',
						},
					]}
					showsVerticalScrollIndicator
					nestedScrollEnabled
				>
					<View style={styles.infoContent}>{/* Add the bonus game rules here. */}</View>
				</IndicatorScrollView>
			</SvaraModal>
		</>
	);
};

const styles = StyleSheet.create({
	gamesRowTop: {
		width: '100%',
		flexDirection: 'row',
		marginTop: -35,
		marginBottom: 15,
		gap: 10,
	},
	gamesRowBottom: {
		width: '100%',
		flexDirection: 'row',
		marginBottom: 10,
		gap: 10,
	},
	collectColumn: {
		flexDirection: 'column',
	},
	collectHeadingWrap: {
		alignItems: 'center',
		justifyContent: 'center',
		marginVertical: 10,
	},
	collectHeadingWrapTight: {
		alignItems: 'center',
		justifyContent: 'center',
		marginVertical: 1,
	},
	collectHeadingText: {
		color: '#fff',
		fontWeight: 'bold',
		textTransform: 'uppercase',
		textShadowColor: 'rgba(0, 0, 0, 0.98)',
		textShadowOffset: { width: 0, height: 3 },
		textShadowRadius: 4,
		letterSpacing: 0.6,
		textAlign: 'center',
	},
	horDivider: {
		width: '100%',
		aspectRatio: 27.5,
	},
	timerSectionPad: {
		paddingTop: 10,
	},
	timerBoxOuter: {
		width: 125,
		height: 34,
		position: 'relative',
		paddingRight: 2,
		marginBottom: 2,
	},
	timerImgScaled: {
		width: '100%',
		aspectRatio: 3.38,
		transform: [{ scale: 1.4 }],
	},
	timerLabel: {
		color: 'white',
		fontWeight: '900',
		fontSize: 13,
		width: '100%',
		textAlign: 'center',
	},
	smallMobileWrap: {
		width: '100%',
		marginTop: -10,
		marginBottom: 8,
		position: 'relative',
	},
	vertDividerWrap: {
		position: 'absolute',
		left: '59%',
		top: 0,
		bottom: 0,
	},
	vertDividerImg: {
		height: '100%',
		aspectRatio: 20 / 1344,
	},
	mobileGamesRow: {
		width: '100%',
		flexDirection: 'row',
	},
	mobileLeftCol: {
		width: '60%',
		paddingHorizontal: 10,
		justifyContent: 'space-evenly',
	},
	mobileRightCol: {
		width: '40%',
		paddingHorizontal: 10,
		alignItems: 'center',
	},
	mobileCollectSection: {
		width: '100%',
	},
	mobileCollectSectionWeb: {
		marginBottom: 8,
	},
	mobileDaysList: {
		width: '100%',
	},
	mobileDaysListWeb: {
		marginTop: 6,
	},
	mobileDayItemWrap: {
		marginBottom: 0,
	},
	mobileDayItemWrapNative: {
		marginBottom: 30,
	},
	mobileDayItemWrapWeb: {
		marginBottom: 6,
	},
	nativeSpacer4: {
		marginTop: 4,
	},
	infoScroll: {
		maxHeight: 500,
	},
	infoScrollContent: {
		paddingTop: 20,
		paddingBottom: 40,
	},
	infoContent: {
		minHeight: 120,
	},
	toastInModal: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 999999,
		elevation: 999999,
	},
});

export default BonusGamesModal;
